import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getClinicId, getClinicSession, listClinicDemoProfiles } from '../api/client';
import { VERTICALS } from '../verticals/catalog';
import { oaAppPath } from './paths';
import { 
  ClipboardPlus, 
  Inbox, 
  Building2, 
  Compass, 
  ArrowRight, 
  User, 
  Settings2, 
  ShieldCheck, 
  Mail, 
  ChevronRight, 
  Activity,
  Network
} from 'lucide-react';

export function OncoAgentHome(): React.ReactElement {
  const { user } = useAuth();
  const [clinicId, setClinicId] = useState<string | null>(null);
  const [clinicName, setClinicName] = useState<string | null>(null);
  const authedClinic = Boolean(getClinicSession());
  const displayName = String(user?.full_name || user?.name || '').trim();
  const email = String(user?.email || '').trim();

  useEffect(() => {
    document.title = 'OncoAgent workspace | ByOnco';
    const id = getClinicId();
    setClinicId(id);
    const profiles = listClinicDemoProfiles();
    const match = profiles.find((p) => p.clinic_id === id) || profiles[0];
    setClinicName(match?.clinic_name || null);
  }, []);

  const nextHref = !displayName
    ? oaAppPath('/settings')
    : !clinicId
      ? oaAppPath('/clinic')
      : oaAppPath('/refer');
  const nextLabel = !displayName
    ? 'Complete your profile'
    : !clinicId
      ? 'Register your clinic'
      : 'Start a referral';

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e6e2d8] pb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="bg-[#0f3e17]/10 text-[#0f3e17] text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider">
              OncoAgent Desk v1.1
            </span>
            {clinicId && (
              <span className="bg-emerald-100 text-emerald-800 text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" /> Secure Clinic Connected
              </span>
            )}
          </div>
          <h1 className="text-3xl font-serif font-semibold text-[#161616] tracking-tight">
            {displayName ? `Welcome back, ${displayName}` : 'Your clinic desk is ready'}
          </h1>
          <p className="text-sm text-[#6d6a63] mt-1 flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" /> Signed in as <strong className="text-[#161616] font-medium">{email || 'your ByOnco account'}</strong>
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link 
            className="inline-flex items-center gap-2 bg-[#0f3e17] hover:bg-[#0c3312] text-white font-medium text-sm px-5 py-2.5 rounded-full transition-all shadow-sm hover:shadow-md" 
            style={{ color: '#ffffff' }}
            to={nextHref}
          >
            <span>{nextLabel}</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Main Premium Welcome Banner */}
      <div className="bg-gradient-to-br from-[#0f3e17] to-[#1b5e28] text-white rounded-2xl p-6 shadow-xl relative overflow-hidden">
        <div className="absolute right-0 bottom-0 opacity-10 transform translate-x-12 translate-y-12 pointer-events-none">
          <Activity className="w-96 h-96" />
        </div>
        <div className="relative z-10 max-w-2xl">
          <p className="text-xs uppercase tracking-widest text-emerald-300 font-bold mb-2">Clinical Operating System</p>
          <h2 className="text-2xl font-serif font-medium mb-2 text-white">Streamline cancer suspicion & referral loops.</h2>
          <p className="text-emerald-100 text-sm leading-relaxed mb-6">
            Capture suspected cases through multi-vertical clinical rules, generate audit-ready, tamper-proof referral packets, and route cases with real-time status isolation across practice boundaries.
          </p>
          <div className="flex flex-wrap gap-4 text-xs text-emerald-100">
            <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-lg backdrop-blur-sm">
              <ShieldCheck className="w-4 h-4 text-emerald-300" />
              <span>Immutable HIPAA-Compliant Handoff</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-lg backdrop-blur-sm">
              <Compass className="w-4 h-4 text-emerald-300" />
              <span>Multi-Pathway Auto-Routing</span>
            </div>
          </div>
        </div>
      </div>

      {!displayName && (
        <div className="bg-[#ffffff] border border-[#e6e2d8] rounded-xl p-5 shadow-sm flex items-start gap-4">
          <div className="bg-[#f6f4ef] p-3 rounded-xl text-[#0f3e17]">
            <User className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-base text-[#161616] mb-1">Finish your profile</h3>
            <p className="text-sm text-[#6d6a63] leading-relaxed mb-4">
              Add your name so outgoing referrals carry the correct clinician identity. Your account remains secure on this device.
            </p>
            <Link className="inline-flex items-center gap-1.5 bg-[#f6f4ef] hover:bg-[#ece8df] text-[#161616] font-medium text-xs px-4 py-2 rounded-lg transition-colors border border-[#e6e2d8]" to={oaAppPath('/settings')}>
              <span>Edit Profile</span>
              <Settings2 className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      )}

      {/* Modern Dashboard Card Grid */}
      <div>
        <h3 className="text-xs uppercase tracking-widest text-[#6d6a63] font-bold mb-4">Workspace Modules</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Link 
            className="group bg-white border border-[#e6e2d8] hover:border-[#0f3e17]/30 rounded-2xl p-5 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 flex gap-4" 
            to={oaAppPath('/refer')}
          >
            <div className="bg-[#f6f4ef] group-hover:bg-[#0f3e17]/10 p-3.5 rounded-xl text-[#0f3e17] h-fit transition-colors">
              <ClipboardPlus className="w-6 h-6" />
            </div>
            <div className="flex-1 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <h4 className="font-semibold text-[#161616] text-lg group-hover:text-[#0f3e17] transition-colors">Refer</h4>
                  <ChevronRight className="w-4 h-4 text-[#6d6a63] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="text-sm text-[#6d6a63] leading-relaxed">
                  Start a clinical referral using guided symptom validation, automatic red flag alerts, and structured schema routing.
                </p>
              </div>
              <span className="text-xs font-semibold text-[#0f3e17] mt-4 flex items-center gap-1">
                Launch referral wizard &rarr;
              </span>
            </div>
          </Link>

          <Link 
            className="group bg-white border border-[#e6e2d8] hover:border-[#0f3e17]/30 rounded-2xl p-5 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 flex gap-4" 
            to={oaAppPath('/inbox')}
          >
            <div className="bg-[#f6f4ef] group-hover:bg-[#0f3e17]/10 p-3.5 rounded-xl text-[#0f3e17] h-fit transition-colors">
              <Inbox className="w-6 h-6" />
            </div>
            <div className="flex-1 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <h4 className="font-semibold text-[#161616] text-lg group-hover:text-[#0f3e17] transition-colors">Inbox</h4>
                  <ChevronRight className="w-4 h-4 text-[#6d6a63] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="text-sm text-[#6d6a63] leading-relaxed">
                  {authedClinic
                    ? 'Manage your receiving queue, audit handoff completeness, request clarifications, and track scheduling.'
                    : 'Acknowledge, triage, and route incoming case packets. Connect to a practice clinic first.'}
                </p>
              </div>
              <span className="text-xs font-semibold text-[#0f3e17] mt-4 flex items-center gap-1">
                {authedClinic ? 'Open clinic queue' : 'Authenticate practice queue'} &rarr;
              </span>
            </div>
          </Link>

          <Link 
            className="group bg-white border border-[#e6e2d8] hover:border-[#0f3e17]/30 rounded-2xl p-5 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 flex gap-4" 
            to={oaAppPath('/clinic')}
          >
            <div className="bg-[#f6f4ef] group-hover:bg-[#0f3e17]/10 p-3.5 rounded-xl text-[#0f3e17] h-fit transition-colors">
              <Building2 className="w-6 h-6" />
            </div>
            <div className="flex-1 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <h4 className="font-semibold text-[#161616] text-lg group-hover:text-[#0f3e17] transition-colors">Clinic settings</h4>
                  <ChevronRight className="w-4 h-4 text-[#6d6a63] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="text-sm text-[#6d6a63] leading-relaxed">
                  {clinicId
                    ? `Active workspace: ${clinicName || 'Connected Clinic'} · ID: ${clinicId}`
                    : 'Configure your practice settings, retrieve API tokens, and invite team members to your secure network.'}
                </p>
              </div>
              <span className="text-xs font-semibold text-[#0f3e17] mt-4 flex items-center gap-1">
                Manage clinic desk &rarr;
              </span>
            </div>
          </Link>

          <Link 
            className="group bg-white border border-[#e6e2d8] hover:border-[#0f3e17]/30 rounded-2xl p-5 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 flex gap-4" 
            to={oaAppPath('/pathways')}
          >
            <div className="bg-[#f6f4ef] group-hover:bg-[#0f3e17]/10 p-3.5 rounded-xl text-[#0f3e17] h-fit transition-colors">
              <Compass className="w-6 h-6" />
            </div>
            <div className="flex-1 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <h4 className="font-semibold text-[#161616] text-lg group-hover:text-[#0f3e17] transition-colors">Clinical Pathways</h4>
                  <ChevronRight className="w-4 h-4 text-[#6d6a63] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="text-sm text-[#6d6a63] leading-relaxed">
                  Browse {VERTICALS.length} evidence-based guidelines and validation schema for early-stage oncology routing and safety events.
                </p>
              </div>
              <span className="text-xs font-semibold text-[#0f3e17] mt-4 flex items-center gap-1">
                Explore protocols &rarr;
              </span>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default OncoAgentHome;
